import { FormEventHandler, ReactNode, useContext, useState } from 'react'
import { Control, FieldErrors, SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'

import { useCallMutation } from '@/common/api/client'
import { BulkCreateW2MasterResponse, BulkCreateW2MasterVars } from '@/common/api/params/w2Master'
import { ApiPath } from '@/common/api/path'
import { MasterType, W2_MASTER_CONFIGS } from '@/common/model/master/w2Master'
import { csvToRecords, parseCsv, validateRecords } from '@/common/util/csv'

import { ConfirmDialogContext } from '@/components/container/modal/dialog/confirm/ConfirmDialogProvider'

export const CSV_EXPORT_MAX_COUNT = 50000

export interface ImportResultMessage {
  rowNo?: number
  messages: (string | ReactNode)[]
}

export const csvUploadSchema = z.object({
  csv: z.custom<File>().refine(
    (file) => {
      if (!file) {
        return true
      }
      return file.type == 'text/csv'
    },
    {
      message: 'CSVのファイル形式が一致しません'
    }
  )
})

export type CsvUploadFormSchemaType = z.infer<typeof csvUploadSchema>

export const CsvUploadMode = {
  FORM: 'FORM',
  RESULT: 'RESULT'
} as const

export type CsvUploadMode = (typeof CsvUploadMode)[keyof typeof CsvUploadMode]

const processText = (text: string, config: { csvHeaders: string[]; requiredCsv: string[] }) => {
  let rows = parseCsv(text)
  // ヘッダー行をスキップ (先頭カラムが csvHeaders[0] と一致する場合)
  if (rows.length > 0 && rows[0][0] === config.csvHeaders[0]) {
    rows = rows.slice(1)
  }
  const { valid, errors } = validateRecords(rows, config.csvHeaders, config.requiredCsv)

  return { valid, errors }
}

const handleFile = (
  file: File,
  config: { csvHeaders: string[]; requiredCsv: string[] },
  onSuccess: (validRows: string[][]) => void,
  onError: (errors: ImportResultMessage[]) => void
) => {
  // UTF-8 で読み込み、文字化けがあれば Shift_JIS にフォールバック
  const reader = new FileReader()
  reader.onload = (ev) => {
    const text = ev?.target?.result
    if (text && typeof text === 'string' && text.includes('\ufffd')) {
      // 置換文字が含まれる → Shift_JIS の可能性が高い
      const reader2 = new FileReader()
      reader2.onload = (ev2) => {
        const result = processText(ev2?.target?.result as string, config)
        if (result.errors.length > 0) {
          onError(result.errors.map((e) => ({ rowNo: e.rowNo, messages: e.messages })))
        } else {
          onSuccess(result.valid)
        }
      }
      reader2.readAsText(file, 'Shift_JIS')
    } else {
      const result = processText(text as string, config)
      if (result.errors.length > 0) {
        onError(result.errors.map((e) => ({ rowNo: e.rowNo, messages: e.messages })))
      } else {
        onSuccess(result.valid)
      }
    }
  }
  reader.readAsText(file, 'UTF-8')
}

const handleCsvFileImport = async (
  file: File,
  siteId: number,
  masterType: MasterType,
  uploadTrigger: (vars: BulkCreateW2MasterVars) => Promise<BulkCreateW2MasterResponse>,
  onComplete: (result: Omit<CsvUploadResult, 'mode'>) => void,
  onError: (errors: ImportResultMessage[]) => void
) => {
  const config = W2_MASTER_CONFIGS[masterType]

  const onCsvRead = async (validRows: string[][]) => {
    const records = csvToRecords(validRows, config.csvHeaders)
    const BATCH_SIZE = 500
    const totalResult: {
      created: number
      updated: number
      errors: ImportResultMessage[]
    } = { created: 0, updated: 0, errors: [] }

    try {
      for (let i = 0; i < records.length; i += BATCH_SIZE) {
        const batch = records.slice(i, i + BATCH_SIZE)
        const response = await uploadTrigger({
          site_id: siteId,
          master_type: masterType,
          // @ts-ignore
          items: batch
        })
        totalResult.created += response.created || 0
        totalResult.updated += response.updated || 0
        if (response.errors)
          totalResult.errors.push(...response.errors.map((e) => ({ rowNo: i + 1, messages: [e] })))
      }
      onComplete({
        total: records.length,
        created: totalResult.created,
        updated: totalResult.updated,
        messages: totalResult.errors
      })
    } catch {
      onComplete({
        total: records.length,
        created: totalResult.created,
        updated: totalResult.updated,
        messages: [...totalResult.errors, { messages: ['アップロード中にエラーが発生しました'] }]
      })
    }
  }
  handleFile(file, config, onCsvRead, onError)
}

export interface CsvUploadResult {
  total: number
  created: number
  updated: number
  mode: CsvUploadMode
  messages: ImportResultMessage[]
}

export const useCsvUploadApiForm = (
  siteId: number,
  masterType: MasterType
): {
  result: CsvUploadResult
  control: Control<CsvUploadFormSchemaType>
  errors: FieldErrors<CsvUploadFormSchemaType>
  onSubmit: FormEventHandler<HTMLFormElement>
  reset: () => void
} => {
  const [result, setResult] = useState<CsvUploadResult>({
    created: 0,
    updated: 0,
    total: 0,
    mode: CsvUploadMode.FORM,
    messages: []
  })

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors }
  } = useForm<CsvUploadFormSchemaType>({
    resolver: zodResolver(csvUploadSchema)
  })

  const onSuccess = (result: Omit<CsvUploadResult, 'mode'>) => {
    setResult({
      ...result,
      mode: CsvUploadMode.RESULT
    })
  }

  const { trigger: uploadTrigger } = useCallMutation<
    BulkCreateW2MasterResponse,
    BulkCreateW2MasterVars
  >(ApiPath.BULK_CREATE_W2_MASTER)

  const { showModal, hide } = useContext(ConfirmDialogContext)

  const onSubmit: SubmitHandler<CsvUploadFormSchemaType> = async (data) => {
    showModal({
      message: 'ファイルをアップロードします。よろしいですか？',
      noText: 'キャンセル',
      yesText: 'アップロード',
      buttonColor: 'primary',
      onClickYes: async () => {
        const { csv, ...values } = data

        return handleCsvFileImport(
          csv,
          siteId,
          masterType,
          uploadTrigger,
          onSuccess,
          (messages) => {
            setResult((result) => ({
              ...result,
              mode: CsvUploadMode.RESULT,
              messages: messages
            }))
          }
        ).then(() => {
          hide()
        })
      }
    })
  }

  return {
    result,
    control,
    errors,
    onSubmit: handleSubmit(onSubmit),
    reset: () => {
      reset()
      setResult((result) => ({
        ...result,
        mode: CsvUploadMode.FORM,
        messages: []
      }))
    }
  }
}
